import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useScrollReveal, fadeUpVariants, staggerContainerVariants } from '../hooks/useScrollReveal'
import { useAdmin } from '../context/AdminContext'
import { FaCode, FaRocket, FaHeart, FaMapMarkerAlt } from 'react-icons/fa'

const highlights = [
  { icon: FaCode,   label: 'Clean Code',  desc: 'Maintainable, documented code following industry best practices.' },
  { icon: FaRocket, label: 'Performance', desc: 'Speed and UX optimization is always my top priority.' },
  { icon: FaHeart,  label: 'Passion',     desc: "I genuinely love building things that make people's lives easier." },
]

export default function About() {
  const { ref, controls } = useScrollReveal()
  const { siteData, loading } = useAdmin()
  const { about } = siteData

  if (loading) return null

  return (
    <section id="about" className="py-28 relative overflow-hidden">
      <Helmet>
        <title>About Me | Shiv Shankar Pandit</title>
        <meta name="description" content="A quick glimpse into who I am and what I do." />
      </Helmet>

      <div className="absolute top-0 right-0 w-72 h-72 rounded-full blur-[110px] opacity-[0.08] pointer-events-none orb-purple" />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div variants={staggerContainerVariants} initial="hidden" animate="visible">

          <motion.div variants={fadeUpVariants} className="mb-14">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <h1 className="section-heading">
                About <span className="gradient-text">Me</span>
              </h1>

            </div>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">

            {/* Bio */}
            <motion.div variants={fadeUpVariants} className="space-y-5">
              {[about.bio1, about.bio2, about.bio3].map((bio, i) => (
                <p key={i} className="text-secondary text-base leading-relaxed">{bio}</p>
              ))}

              {/* Quick facts */}
              <div className="grid grid-cols-2 gap-3 pt-3">
                {(Array.isArray(about.facts) ? about.facts : []).map(({ label, value }) => (
                  <div key={label} className="card p-4" style={{ borderRadius: '1rem' }}>
                    <p className="field-label" style={{ marginBottom: '3px' }}>{label}</p>
                    <p className="text-sm font-semibold text-primary">{value}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Highlight cards */}
            <motion.div variants={fadeUpVariants} className="space-y-4">
              {highlights.map(({ icon: Icon, label, desc }) => (
                <motion.div key={label}
                  className="card p-5 flex items-start gap-4"
                  whileHover={{ x: 5, boxShadow: 'var(--shadow-glow)' }}
                  transition={{ duration: 0.2 }}>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg,rgba(59,130,246,.12),rgba(16,185,129,.12))' }}>
                    <Icon size={16} style={{ color: 'var(--accent-1)' }} />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-primary mb-1">{label}</h3>
                    <p className="text-secondary text-sm leading-relaxed">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
